import { useRef, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Music, Camera, Loader2, Star } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useMedia } from "../../context/MediaContext";
import { uploadMedia, resolveMedia, apiError } from "../../lib/api";
import { slug, initials, EVENT_ISO } from "./prideData";

export const RAINBOW = "linear-gradient(90deg,#e40303,#ff8c00,#ffed00,#008026,#004dff,#750787)";

export function RainbowBar({ className = "" }) {
  return <div aria-hidden className={`h-1.5 w-full ${className}`} style={{ backgroundImage: RAINBOW }} data-testid="rainbow-bar" />;
}

export function RainbowArc({ className = "" }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute rounded-full opacity-20 blur-2xl ${className}`}
      style={{ background: "conic-gradient(from 180deg,#e40303,#ff8c00,#ffed00,#008026,#004dff,#750787,#e40303)" }} />
  );
}

const TABS = [
  { to: "/pride", label: "Overview", end: true },
  { to: "/pride/whats-on", label: "What's on" },
  { to: "/pride/lineup", label: "Main stage" },
  { to: "/pride/parade", label: "Pride parade" },
  { to: "/pride/visit", label: "Visit" },
  { to: "/pride/contact", label: "Contact" },
];

export function SubNav() {
  return (
    <div className="sticky top-[74px] z-30 border-b border-black/[0.06] bg-white/85 backdrop-blur-md">
      <div className="container-x flex gap-1 overflow-x-auto py-2.5 no-scrollbar">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`pride-nav-${slug(t.label)}`}
            className={({ isActive }) =>
              `whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-nead-pink text-white" : "text-nead-navy/65 hover:bg-black/5 hover:text-nead-navy"}`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>
      <RainbowBar />
    </div>
  );
}

function diff() {
  const d = Math.max(0, new Date(EVENT_ISO).getTime() - Date.now());
  return {
    days: Math.floor(d / 86400000),
    hours: Math.floor((d / 3600000) % 24),
    mins: Math.floor((d / 60000) % 60),
    secs: Math.floor((d / 1000) % 60),
  };
}

export function Countdown() {
  const [t, setT] = useState(diff());
  useEffect(() => {
    const id = setInterval(() => setT(diff()), 1000);
    return () => clearInterval(id);
  }, []);
  const units = [
    { label: "Days", value: t.days },
    { label: "Hours", value: t.hours },
    { label: "Mins", value: t.mins },
    { label: "Secs", value: t.secs },
  ];
  return (
    <div className="flex gap-3 sm:gap-4" data-testid="pride-countdown">
      {units.map((u) => (
        <div key={u.label} className="min-w-[64px] rounded-2xl border border-white/15 bg-white/5 px-3 py-3 text-center backdrop-blur sm:min-w-[80px]">
          <div className="font-display font-bold text-3xl sm:text-4xl text-white tabular-nums leading-none">{String(u.value).padStart(2, "0")}</div>
          <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-widest text-nead-cyan">{u.label}</div>
        </div>
      ))}
    </div>
  );
}

export function LineupCard({ name, photo, grad, headline = false }) {
  const { user } = useAuth();
  const { get, set } = useMedia();
  const isAdmin = user && user.role === "admin";
  const key = `pride.lineup.${slug(name)}`;
  const override = get(key, "");
  const src = override ? resolveMedia(override) : photo;
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadMedia(key, file);
      set(key, url);
    } catch (err) { window.alert(apiError(err)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className={`group relative aspect-square overflow-hidden rounded-2xl ${headline ? "ring-2 ring-nead-pink" : ""}`} data-testid={`lineup-${slug(name)}`}>
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full w-full items-center justify-center transition-transform duration-500 group-hover:scale-105" style={{ backgroundImage: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}>
          <span className="font-display font-black text-white/90 text-4xl sm:text-5xl tracking-tight drop-shadow">{initials(name)}</span>
        </div>
      )}
      {headline && (
        <span className="absolute top-2.5 left-2.5 z-10 inline-flex items-center gap-1 rounded-full bg-nead-pink px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
          <Star className="h-3 w-3 fill-white" /> Headliner
        </span>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-4 pt-10">
        <div className="flex items-center gap-1.5">
          <Music className="h-3.5 w-3.5 text-white/80" />
          <span className="font-display font-bold text-white text-sm leading-tight">{name}</span>
        </div>
      </div>
      {isAdmin && (
        <>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={onFile} data-testid={`lineup-input-${slug(name)}`} />
          <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`lineup-replace-${slug(name)}`}
            className="absolute bottom-2.5 right-2.5 z-20 inline-flex items-center gap-1.5 rounded-full bg-nead-ink/85 px-2.5 py-1.5 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur hover:bg-nead-pink">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Photo
          </button>
        </>
      )}
    </div>
  );
}
